import { useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Mail, ExternalLink, UserCircle } from "lucide-react";

interface Expert {
  id: number;
  name: string;
  title: string;
  organization: string;
  fields: string[];
  bio: string;
  profileImage: string;
  location: string;
  contact: {
    email: string;
    website?: string;
    twitter?: string;
  };
  publications?: string[];
}

// Sample experts data
const expertsData: Expert[] = [
  {
    id: 1,
    name: "Dr. Sarah Chen",
    title: "Director of AI Ethics",
    organization: "Global Fact-Check Institute",
    fields: ["AI Ethics", "Computational Journalism", "NLP"],
    bio: "Dr. Chen specializes in the ethical implications of AI in media environments. Her research focuses on algorithmic bias detection and mitigation strategies for automated content classification systems, particularly in the context of misinformation detection.",
    profileImage: "https://picsum.photos/id/77/200",
    location: "San Francisco, USA",
    contact: {
      email: "s.chen@example.org",
      website: "sarahchen.research.org",
      twitter: "@DrSarahChen"
    },
    publications: [
      "Ethical Boundaries in AI-Driven Content Moderation (2024)",
      "Bias Detection Models for Automated Fact-Checking Systems (2023)",
      "The Limitations of Large Language Models in News Classification (2022)"
    ]
  },
  {
    id: 2,
    name: "Prof. Miguel Estrada",
    title: "Lead Researcher",
    organization: "Digital Trust Alliance",
    fields: ["Deepfake Detection", "Media Forensics", "Computer Vision"],
    bio: "Prof. Estrada is a pioneer in deepfake detection technologies. His team has developed state-of-the-art models for identifying manipulated visual media with over 97% accuracy in controlled settings. He consults for major platforms on content authenticity verification systems.",
    profileImage: "https://picsum.photos/id/91/200",
    location: "Barcelona, Spain",
    contact: {
      email: "mestrada@example.edu",
      website: "estrada-lab.edu"
    },
    publications: [
      "Advanced Deepfake Detection: Beyond Visual Artifacts (2024)",
      "Temporal Inconsistencies in Manipulated Video Content (2023)",
      "Multi-modal Approaches to Media Authentication (2022)"
    ]
  },
  {
    id: 3,
    name: "Amira Nwosu",
    title: "Chief Security Officer",
    organization: "CyberTrust Securities",
    fields: ["Cybersecurity", "Digital Forensics", "Threat Intelligence"],
    bio: "Amira leads cybersecurity efforts against coordinated disinformation campaigns. With 15+ years of experience in the field, she specializes in tracking state-sponsored information operations and developing countermeasures against automated bot networks.",
    profileImage: "https://picsum.photos/id/65/200",
    location: "London, UK",
    contact: {
      email: "anwosu@example.com",
      twitter: "@AmiraNwosu"
    }
  },
  {
    id: 4,
    name: "Dr. Thomas Weber",
    title: "Senior Fellow",
    organization: "Information Integrity Foundation",
    fields: ["Political Misinformation", "Electoral Systems", "Network Analysis"],
    bio: "Dr. Weber studies the spread of political misinformation across social networks. His research has been instrumental in developing early warning systems for coordinated inauthentic behavior during electoral periods in multiple democracies.",
    profileImage: "https://picsum.photos/id/41/200",
    location: "Berlin, Germany",
    contact: {
      email: "weber@example.org",
      website: "weber-research.org"
    },
    publications: [
      "Network Topology Patterns in Misinformation Spread (2024)",
      "Temporal Detection of Coordinated Information Operations (2023)",
      "Quantifying Influence Operations in Democratic Processes (2022)"
    ]
  },
  {
    id: 5,
    name: "Lin Mei",
    title: "Lead Data Scientist",
    organization: "Truth Metrics Labs",
    fields: ["Machine Learning", "Statistical Analysis", "Automated Fact-Checking"],
    bio: "Lin specializes in developing automated fact-checking systems that combine natural language processing with structured knowledge graphs. Her award-winning ClaimVerify model has been implemented by news organizations worldwide.",
    profileImage: "https://picsum.photos/id/29/200",
    location: "Singapore",
    contact: {
      email: "lin.mei@example.com",
      website: "linmei.tech",
      twitter: "@LinMeiTech"
    }
  },
  {
    id: 6,
    name: "Dr. James Okafor",
    title: "Research Director",
    organization: "African Digital Media Observatory",
    fields: ["Media Literacy", "Misinformation Intervention", "Cultural Context"],
    bio: "Dr. Okafor's research focuses on culturally-relevant approaches to media literacy and misinformation interventions. His work has been crucial in developing region-specific strategies for combating viral misinformation in various African countries.",
    profileImage: "https://picsum.photos/id/86/200",
    location: "Nairobi, Kenya",
    contact: {
      email: "jokafor@example.org",
      twitter: "@DrJamesOkafor"
    },
    publications: [
      "Contextual Approaches to Media Literacy Education (2024)",
      "Misinformation in Multilingual Communities (2023)",
      "Cultural Factors in Disinformation Resilience (2022)"
    ]
  }
];

// Domain expertise categories
const expertiseAreas = [
  "AI Ethics", 
  "Computational Journalism", 
  "NLP", 
  "Deepfake Detection", 
  "Media Forensics", 
  "Computer Vision",
  "Cybersecurity", 
  "Digital Forensics",
  "Threat Intelligence", 
  "Political Misinformation",
  "Electoral Systems",
  "Network Analysis",
  "Machine Learning",
  "Statistical Analysis",
  "Automated Fact-Checking",
  "Media Literacy",
  "Misinformation Intervention",
  "Cultural Context"
];

const Experts = () => {
  const [experts, setExperts] = useState<Expert[]>(expertsData);
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"grid" | "list" | "detail">("grid");
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search logic would normally connect to a backend
    // For demo, we'll filter the existing data
    if (!searchQuery.trim()) {
      setExperts(expertsData);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = expertsData.filter(expert => 
        expert.name.toLowerCase().includes(query) || 
        expert.organization.toLowerCase().includes(query) ||
        expert.fields.some(field => field.toLowerCase().includes(query)) ||
        expert.bio.toLowerCase().includes(query)
      );
      setExperts(filtered);
    }
  };

  const handleFilterByExpertise = (expertise: string | null) => {
    setSelectedFilter(expertise);
    
    if (!expertise) {
      setExperts(expertsData);
    } else {
      const filtered = expertsData.filter(expert => 
        expert.fields.some(field => field === expertise)
      );
      setExperts(filtered);
    }
  };

  const handleExpertSelect = (expert: Expert) => {
    setSelectedExpert(expert);
    setActiveTab("detail");
  };

  const handleBackToList = () => {
    setActiveTab(activeTab === "detail" ? "grid" : "grid");
    setSelectedExpert(null);
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Experts</h1>
          <p className="mt-2 text-gray-400">Connect with misinformation and OSINT experts</p>
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Misinformation Experts</h1>
            <p className="text-gray-400 mt-2">
              Connect with leading researchers and professionals in the fight against misinformation.
            </p>
          </div>
          
          <form onSubmit={handleSearch} className="flex w-full md:w-auto">
            <div className="relative flex-grow">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Search experts..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Filter sidebar */}
          <div className="w-full md:w-64 shrink-0 space-y-4">
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle className="text-lg">Filter By Expertise</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant={selectedFilter === null ? "default" : "ghost"}
                  className={`w-full justify-start ${selectedFilter === null ? "bg-truth" : ""}`}
                  onClick={() => handleFilterByExpertise(null)}
                >
                  All Areas
                </Button>
                {expertiseAreas.map(area => (
                  <Button
                    key={area}
                    variant={selectedFilter === area ? "default" : "ghost"}
                    className={`w-full justify-start text-sm ${selectedFilter === area ? "bg-truth" : ""}`}
                    onClick={() => handleFilterByExpertise(area)}
                  >
                    {area}
                  </Button>
                ))}
              </CardContent>
            </Card>
            
            <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
              <CardHeader>
                <CardTitle className="text-lg">Need Assistance?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-400">
                  Looking for a specific type of expert not listed here?
                </p>
                <Button className="w-full bg-truth hover:bg-truth-dark">
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="flex-grow">
            {activeTab === "detail" && selectedExpert ? (
              <ExpertDetail expert={selectedExpert} onBack={handleBackToList} />
            ) : (
              <div className="space-y-4">
                <Tabs defaultValue="grid" value={activeTab} onValueChange={(value) => setActiveTab(value as "grid" | "list")}>
                  <div className="flex justify-between items-center">
                    <TabsList>
                      <TabsTrigger value="grid">Grid View</TabsTrigger>
                      <TabsTrigger value="list">List View</TabsTrigger>
                    </TabsList>
                    <div className="text-sm text-gray-400">
                      Showing {experts.length} experts
                    </div>
                  </div>
                  
                  <TabsContent value="grid" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {experts.map(expert => (
                        <Card 
                          key={expert.id} 
                          className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/30 cursor-pointer transition-all"
                          onClick={() => handleExpertSelect(expert)}
                        >
                          <CardContent className="pt-6">
                            <div className="flex flex-col items-center text-center">
                              <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-truth/30">
                                <img 
                                  src={expert.profileImage} 
                                  alt={expert.name} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <h3 className="font-bold text-lg">{expert.name}</h3>
                              <p className="text-sm text-gray-400 mb-1">{expert.title}</p>
                              <p className="text-sm text-truth mb-3">{expert.organization}</p>
                              <div className="flex flex-wrap justify-center gap-1 mb-3">
                                {expert.fields.slice(0, 3).map((field, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                              <Button variant="outline" className="mt-2 text-sm w-full">
                                View Profile
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    
                    {experts.length === 0 && (
                      <div className="text-center py-12 bg-black/20 border border-gray-800 rounded-lg">
                        <UserCircle className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                        <h3 className="text-lg font-medium">No experts match your search</h3>
                        <p className="text-gray-400 mt-2">Try adjusting your search terms or filters</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedFilter(null);
                            setExperts(expertsData);
                          }}
                        >
                          Reset All Filters
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="list" className="mt-6 space-y-4">
                    {experts.map(expert => (
                      <Card 
                        key={expert.id} 
                        className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/30 cursor-pointer transition-all"
                        onClick={() => handleExpertSelect(expert)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 border-2 border-truth/30">
                              <img 
                                src={expert.profileImage} 
                                alt={expert.name} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-grow">
                              <h3 className="font-bold">{expert.name}</h3>
                              <p className="text-sm text-gray-400">{expert.title}, {expert.organization}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {expert.fields.slice(0, 3).map((field, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Button variant="outline" size="sm">
                                View Profile
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {experts.length === 0 && (
                      <div className="text-center py-12 bg-black/20 border border-gray-800 rounded-lg">
                        <UserCircle className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                        <h3 className="text-lg font-medium">No experts match your search</h3>
                        <p className="text-gray-400 mt-2">Try adjusting your search terms or filters</p>
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedFilter(null);
                            setExperts(expertsData);
                          }}
                        >
                          Reset All Filters
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ExpertDetailProps {
  expert: Expert;
  onBack: () => void;
}

const ExpertDetail = ({ expert, onBack }: ExpertDetailProps) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <Button 
        variant="ghost" 
        onClick={onBack} 
        className="flex items-center gap-2 -ml-2"
      >
        Back to experts
      </Button>
      
      <Card className="bg-black/20 backdrop-blur-sm border-gray-800 overflow-hidden">
        <div className="md:flex">
          <div className="md:w-1/3 p-6 flex flex-col items-center text-center">
            <div className="w-32 h-32 rounded-full overflow-hidden mb-4 border-2 border-truth/30">
              <img 
                src={expert.profileImage} 
                alt={expert.name} 
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-2xl font-bold">{expert.name}</h2>
            <p className="text-truth">{expert.title}</p>
            <p className="text-sm">{expert.organization}</p>
            <p className="text-sm text-gray-400 mt-2">{expert.location}</p>
            
            <div className="mt-6 space-y-3 w-full">
              <Button 
                className="w-full flex items-center gap-2 bg-truth hover:bg-truth-dark"
              >
                <Mail className="h-4 w-4" /> Contact
              </Button>
              
              {expert.contact.website && (
                <Button 
                  variant="outline" 
                  className="w-full flex items-center gap-2"
                  onClick={() => window.open(`https://${expert.contact.website}`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" /> Website
                </Button>
              )}
            </div>
          </div>
          
          <div className="md:w-2/3 p-6 border-t md:border-t-0 md:border-l border-gray-800">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Areas of Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {expert.fields.map((field, idx) => (
                    <Badge key={idx} className="bg-truth/20 text-truth hover:bg-truth/30">
                      {field}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">About</h3>
                <p className="text-gray-300">
                  {expert.bio}
                </p>
              </div>
              
              {expert.publications && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Key Publications</h3>
                  <ul className="space-y-2">
                    {expert.publications.map((pub, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <ExternalLink className="h-4 w-4 mt-1 text-truth shrink-0" />
                        <span className="text-gray-300">{pub}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-truth" />
                    <span>{expert.contact.email}</span>
                  </div>
                  {expert.contact.twitter && (
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-truth">
                        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                      </svg>
                      <span>{expert.contact.twitter}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Experts;
